import Button from "@/common/elements/Button";
import CommonDatePicker from "@/common/elements/DatePicker";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import Modal from "@/common/elements/Modal";
import Table from "@/common/elements/Table";
import { StyledGridCol, StyledGridRow } from "@/common/styles";
import { useEffect, useState } from "react";
import { BookingsSectionTitle } from "./styles";
import PersonalBasicInformation from "./PersonalInformationDetails/PersonalBasicInformation";
import AliasesList from "./PersonalInformationDetails/AliasesList";
import AddressesList from "./PersonalInformationDetails/AddressesList";
import {
  getOptionsFromRefList,
  hasCheckSecurity,
} from "@/util/common_function";
import { Form, Popconfirm } from "antd";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import { v4 as uuidv4 } from "uuid";
import API_ENDPOINTS from "@/util/api";
import FolderDetailSearchModal from "@/components/FolderDetailSearch/FolderDetailSearchModal";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";

const BookingsMovementRules = () => {
  const [movementRuleForm] = Form.useForm();
  const dispatch = useDispatch();
  const inmateMovementRuleList = useSelector(
    (state) => state.bookings?.bookings?.inmateMovementRule
  );
  const { bookingsFieldsEnabled } = useSelector((state) => state?.bookings);
  const [isFolderSearchModal, setIsFolderSearchModal] = useState(false);
  const [ruleList, setRuleList] = useState(inmateMovementRuleList || []);
  const [selectedRow, setSelectedRow] = useState();
  const [showModal, setShowModal] = useState(false);
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [ruleType, setRuleType] = useState(null);
  const [keepSeparate, setKeepSeparate] = useState(null);
  const [formData, setFormData] = useState();
  const [isEdit, setIsEdit] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [inmateMovementRuleID, setInmateMovementRuleID] = useState(null);
  const [tableData, setTableData] = useState({
    headers: [
      { key: "summaryDescription", text: "Summary Description" },
      { key: "currentLocation", text: "Current Location" },
    ],
    rows: [],
  });

  const [selectedAliases, setSelectedAliases] = useState(
    selectedRow?.jackets?.[0]?.bio?.[0]?.bioAliases || []
  );
  const [selectedAddresses, setSelectedAddresses] = useState(
    selectedRow?.jackets?.[0]?.bio?.[0]?.bioAddresses || []
  );

  const fullSearch = useSelector(state => state.fullSearch);

  useEffect(() => {
    buildTableData(ruleList);
    updateMovementRule();
  }, [ruleList]);

  useEffect(() => {
    setSelectedAliases(selectedRow?.jackets?.[0]?.bio?.[0]?.bioAliases || []);
    setSelectedAddresses(
      selectedRow?.jackets?.[0]?.bio?.[0]?.bioAddresses || []
    );
    movementRuleForm.setFieldsValue(selectedRow);
  }, [selectedRow]);

  useEffect(() => {
    const personalData =
      movementRuleForm.getFieldsValue([
        "lName",
        "fName",
        "mName",
        "suffix",
        "daytimePhone",
        "phone",
        "dob",
        "dlExpiration",
      ]) || {};
    const jacketsPayload = [
      {
        bio: [
          {
            ...personalData,
            bioAliases: selectedAliases,
            bioAddresses: selectedAddresses,
          },
        ],
      },
    ];
    movementRuleForm.setFieldsValue({ jackets: jacketsPayload });
  }, [selectedAliases, selectedAddresses, movementRuleForm]);

  const handleModal = (val) => {
    if (val == "edit") {
      setIsEdit(true);
    }
    setShowModal(!showModal);
    if (selectedRow) {
      setKeepSeparate(selectedRow?.keepSeparateTypeID || null);
    } else {
      setKeepSeparate(null);
    }
    setIsRowSelected(false);
  };

  const handleRuleTypeChange = (value) => {
    setRuleType(value);
    movementRuleForm.setFieldValue("inmateMovementCommonID", null);
    movementRuleForm.setFieldValue("keepSeparateTypeID", null);
  };

  const keepSeparateChange = (value) => {
    setKeepSeparate(value);
    if (value === 54030002) {
      setIsFolderSearchModal(true);
    } else {
      setIsFolderSearchModal(false);
    }
  };

  const buildTableData = (rules) => {
    const tableRowList = [];
    rules?.forEach((item) => {
      const list = { ...item };
      if (item?.inmateMovementCommonID) {
        list["summaryDescription"] = `${
          getOptionsFromRefList(
            "Inmate_Movement_Common",
            false,
            false,
            true
          ).filter((data) => item?.inmateMovementCommonID === data?.value)?.[0]
            ?.label
        } ${item?.comments?.trim() ? item?.comments?.trim() : ""}`;
      } else if (
        item?.keepSeparateTypeID ||
        item?.inmateMovementRuleTypeID === 54020001
      ) {
        list["summaryDescription"] = item?.keepSeparateTypeID
          ? `Keep Separate from ${
              getOptionsFromRefList(
                "Keep_Separate_Type",
                false,
                false,
                true
              ).filter((data) => item?.keepSeparateTypeID === data?.value)?.[0]
                ?.label
            }. ${item?.comments?.trim() ? item?.comments?.trim() : ""}`
          : `Keep Separate from. ${
              item?.comments?.trim() ? item?.comments?.trim() : ""
            }`;
      } else {
        list["summaryDescription"] = item?.comments;
      }
      list["currentLocation"] = "";
      list["movementRulId"] = item?.inmateMovementRuleID || item?.id;
      tableRowList.push(list);
    });
    setTableData((prev) => ({ ...prev, rows: tableRowList }));
  };

  const addRuleType = () => {
    setKeepSeparate(null);
    setSelectedRow({
      expirationDate: null,
      inmateMovementRuleTypeID: null,
      inmateMovementCommonID: null,
      keepSeparateTypeID: null,
      comments: null,
    });
    setInmateMovementRuleID(null);
    handleModal();
    setRuleType(null);
  };

  const patchSelectedRow = (data) => {
    setInmateMovementRuleID(data?.inmateMovementRuleID || data?.id);
    const rowData = ruleList?.filter(
      (item) =>
        item?.inmateMovementRuleID === data?.movementRulId ||
        item?.id === data?.movementRulId
    )?.[0];
    setSelectedRow({
      ...rowData,
      expirationDate: rowData?.expirationDate
        ? dayjs(rowData?.expirationDate)
        : null,
    });
    setIsRowSelected(true);
    setRuleType(rowData?.inmateMovementRuleTypeID);
  };

  const deleteMovementRule = () => {
    const tempArray = ruleList?.filter(
      (item) =>
        (item?.inmateMovementRuleID &&
          item?.inmateMovementRuleID !== inmateMovementRuleID) ||
        (item?.id && item?.id !== inmateMovementRuleID)
    );
    setRuleList([...tempArray]);
    const url = API_ENDPOINTS.movementRule + "/id/" + inmateMovementRuleID;
    dispatch(setDeleteAPis(url));
    setSelectedRow(null);
    setIsRowSelected(false);
  };

  const updateRowData = (oldData, newData) => {
    return {
      ...oldData,
      expirationDate: newData?.expirationDate
        ? dayjs(newData?.expirationDate).format("MM/DD/YYYY")
        : null,
      inmateMovementRuleTypeID: newData?.inmateMovementRuleTypeID,
      inmateMovementCommonID: newData?.inmateMovementCommonID,
      keepSeparateTypeID: newData?.keepSeparateTypeID,
      comments: newData?.comments,
      jackets: newData?.jackets,
    };
  };

  const saveInmateRule = () => {
    const personalData =
      movementRuleForm.getFieldsValue([
        "lName",
        "fName",
        "mName",
        "suffix",
        "daytimePhone",
        "phone",
        "dob",
        "dlExpiration",
      ]) || {};
    const jacketsPayload = [
      {
        bio: [
          {
            ...personalData,
            bioAliases: selectedAliases,
            bioAddresses: selectedAddresses,
          },
        ],
      },
    ];
    movementRuleForm.setFieldsValue({ jackets: jacketsPayload });

    movementRuleForm
      .validateFields()
      .then((values) => {
        if (inmateMovementRuleID) {
          setRuleList((prev) =>
            prev.map((p) =>
              p?.inmateMovementRuleID === inmateMovementRuleID ||
              p?.id === inmateMovementRuleID
                ? updateRowData(p, values)
                : p
            )
          );
        } else {
          setRuleList((prev) => [
            ...prev,
            {
              ...values,
              id: uuidv4(),
              expirationDate: values?.expirationDate
                ? dayjs(values?.expirationDate).format("MM/DD/YYYY")
                : null,
            },
          ]);
        }
        handleModal();
      })
      .catch((err) => console.log(err));
  };

  const updateMovementRule = () => {
    dispatch(
      setBookings({
        inmateMovementRule: [...ruleList],
      })
    );
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (newValue.trim() === "") {
      movementRuleForm.setFieldValue("comments", null);
    }
  };

  const handleSelectCurrentJacket = (selectedFolder) => {
    if (!selectedFolder) {
      alert("Please select a folder from the list first.");
      return;
    }
    const folderData = selectedFolder.originalData;
    const jacket = folderData?.jackets
      ? folderData?.jackets
      : { staffJacket: false, orphanFlag: false, bio: [selectedFolder] };
    const nonPersonalKeys = [
      "inmateMovementRuleTypeID",
      "inmateMovementCommonID",
      "keepSeparateTypeID",
      "comments",
      "expirationDate",
      "jackets",
    ];
    const currentNonPersonal = movementRuleForm.getFieldsValue(nonPersonalKeys);

    const updatedRow = {
      ...currentNonPersonal,
      keepSeparateTypeID: 54030002,
      jackets: [jacket],
    };

    setSelectedRow(updatedRow);
    movementRuleForm.setFieldsValue(updatedRow);
    setIsFolderSearchModal(false);
  };
  useEffect(() => {
    if (isEdit) {
      let isShow = hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.MODIFY,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report
      );
      if (isShow) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    } else {
      setIsDisabled(false);
    }
  }, [isEdit]);
  return (
    <>
      <StyledGridRow
        gutter={16}
        justify="space-between"
        style={{ marginBottom: "15px" }}
      >
        <StyledGridCol>
          <Button
            color="primary"
            type="primary"
            onClick={addRuleType}
            disabled={
              !bookingsFieldsEnabled ||
              !hasCheckSecurity(
                Security_Types.functionality,
                Entity_types.subModule,
                LABELS_CAPITAL.ADD,
                MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                SUB_MODULES_LIST_WITH_ID.Arrest_Report
              )
            }
          >
            Add Movement Rules
          </Button>
        </StyledGridCol>
        <StyledGridCol>
          <Button
            color="primary"
            disabled={
              !isRowSelected ||
              tableData?.rows?.length === 0 ||
              !hasCheckSecurity(
                Security_Types.functionality,
                Entity_types.subModule,
                LABELS_CAPITAL.VIEW,
                MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                SUB_MODULES_LIST_WITH_ID.Arrest_Report
              )
            }
            type="primary"
            onClick={() => handleModal("edit")}
          >
            Details
          </Button>
          <Popconfirm
            title="Delete the Movement Rules"
            description="Are you sure to delete this Movement Rule?"
            onConfirm={deleteMovementRule}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            <Button
              color="primary"
              type="primary"
              style={{ marginLeft: "10px" }}
              disabled={
                !bookingsFieldsEnabled ||
                !isRowSelected ||
                tableData?.rows?.length === 0 ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.subModule,
                  LABELS_CAPITAL.DELETE,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  SUB_MODULES_LIST_WITH_ID.Arrest_Report
                )
              }
            >
              Delete
            </Button>
          </Popconfirm>
        </StyledGridCol>
      </StyledGridRow>

      {hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report
      ) && (
        <Table
          headers={tableData?.headers}
          data={tableData?.rows}
          onRowClick={(data) => patchSelectedRow(data)}
          onRowDoubleClick={(data) => {
            patchSelectedRow(data);
            handleModal("edit");
          }}
          selected={selectedRow === null}
        />
      )}

      <Modal
        header="Inmate Movement Rule"
        size="xl"
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setRuleType(null);
          setKeepSeparate(null);
          setSelectedRow(null);
          setIsEdit(false);
        }}
        footerActions={[
          {
            key: "save",
            text: "Save",
            onClick: saveInmateRule,
            disabled: !bookingsFieldsEnabled || isDisabled,
          },
        ]}
        footerCloseAction={() => setIsEdit(false)}
        footerCloseActionText={"Cancel"}
      >
        <Form
          form={movementRuleForm}
          initialValues={formData}
          disabled={!bookingsFieldsEnabled}
        >
          <StyledGridRow gutter={16}>
            <StyledGridCol span={6}>
              <FormItem
                label="Rule Type"
                name="inmateMovementRuleTypeID"
                required={!fullSearch}
                rules={
                  !fullSearch
                  ? [
                    {
                      required: true,
                      message: "Rule Type is required",
                    },
                  ] : []
                }
              >
                <Select
                  options={getOptionsFromRefList(
                    "Inmate_Movement_Rule_Type",
                    false,
                    false,
                    true
                  )}
                  placeholder="Rule Type"
                  showSearch={true}
                  variant="outlined"
                  onChange={(e) => {
                    // setTimeout(() => document.activeElement.blur(), 0);
                    handleRuleTypeChange(e);
                  }}
                  disabled={!bookingsFieldsEnabled}
                />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol span={6}>
              <FormItem label="Expiration Date" name="expirationDate">
                <CommonDatePicker showTime={false} format="MM/DD/YYYY" disabled={!bookingsFieldsEnabled} />
              </FormItem>
            </StyledGridCol>
            {ruleType === 54020003 && (
              <StyledGridCol span={6}>
                <FormItem label="Common Rules" name="inmateMovementCommonID">
                  <Select
                    options={getOptionsFromRefList(
                      "Inmate_Movement_Common",
                      false,
                      false,
                      true
                    )}
                    placeholder="Common Rules"
                    showSearch={true}
                    variant="outlined"
                    disabled={!bookingsFieldsEnabled}
                    // onChange={() =>
                    //   setTimeout(() => document.activeElement.blur(), 0)
                    // }
                  />
                </FormItem>
              </StyledGridCol>
            )}
            {ruleType === 54020001 && (
              <StyledGridCol span={6}>
                <FormItem label="Keep Separate From" name="keepSeparateTypeID">
                  <Select
                    options={getOptionsFromRefList(
                      "Keep_Separate_Type",
                      false,
                      false,
                      true
                    )}
                    placeholder="Keep Separate From"
                    showSearch={true}
                    variant="outlined"
                    onChange={(e) => {
                      // setTimeout(() => document.activeElement.blur(), 0);
                      keepSeparateChange(e);
                    }}
                    disabled={!bookingsFieldsEnabled}
                  />
                </FormItem>
              </StyledGridCol>
            )}
          </StyledGridRow>

          {keepSeparate === 54030002 && (
            <StyledGridRow>
              <StyledGridCol span={24}>
                <BookingsSectionTitle>
                  <span>Personal Information</span>
                </BookingsSectionTitle>
                <PersonalBasicInformation
                  selectedAssociate={selectedRow?.jackets?.[0]?.bio?.[0]}
                  form={movementRuleForm}
                  associates={true}
                />
                <AliasesList
                  showQuickAddForm={false}
                  selectedAliases={selectedAliases}
                  associates={true}
                  onAliasesChange={setSelectedAliases}
                />
                <FormItem name="bioAliases" hidden>
                  <TextField />
                </FormItem>
                <AddressesList
                  selectedAddresses={selectedAddresses}
                  associates={true}
                  onAddressesChange={setSelectedAddresses}
                />
                <FormItem name="bioAddresses" hidden>
                  <TextField />
                </FormItem>
                <FormItem name="jackets" hidden>
                  <TextField />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>
          )}
          <StyledGridRow>
            <StyledGridCol span={18}>
              <BookingsSectionTitle>
                <span>Other Information</span>
              </BookingsSectionTitle>
              <FormItem
                name="comments"
                onChange={handleChange}
                rules={[
                  {
                    required:
                      ruleType === 54020002 || keepSeparate === 54030003,
                    message: "Other Information is required",
                  },
                ]}
              >
                <TextField
                  inputType="textarea"
                  rows="3"
                  onChange={(e) => {
                    if (e.target.value.trim()) {
                      return false;
                    } else {
                      return true;
                    }
                  }}
                />
              </FormItem>
            </StyledGridCol>
          </StyledGridRow>
        </Form>
      </Modal>

      {isFolderSearchModal && (
        <FolderDetailSearchModal
          show={isFolderSearchModal}
          onHide={() => setIsFolderSearchModal(false)}
          onSaveClick={handleSelectCurrentJacket}
        />
      )}
    </>
  );
};

export default BookingsMovementRules;
